/* eslint-disable ember/no-runloop */

import type ModelRegistry from "@ember-data/types/registries/model";
import { getOwner } from "@ember/-internals/owner";
import type Owner from "@ember/owner";
import type RouterService from "@ember/routing/router-service";
import type UpstreamTransition from "@ember/routing/transition";
import { next } from "@ember/runloop";
import { service } from "@ember/service";
import Component from "@glimmer/component";
import { task } from "ember-concurrency";
import type ApplicationAdapter from "parog-web/adapters/application";
import FormAction from "parog-web/components/forc/form-action";
import type errors from "parog-web/services/errors";

interface Transition extends UpstreamTransition {
  isAborted: boolean;
}

export interface ForcBaseArgs {
  submitButtonTitle?: string;
  cancelButtonTitle?: string;
  shouldCheckDirtyness?: boolean;
  hasSmallFooter?: boolean;
  formAction?: FormAction;
  patient: ModelRegistry["patient"];
  isReadOnly?: boolean;
  originJournalEventType?: string;
  originJournalEventId?: string;
  afterCancel?: () => void;
  afterCompleted?: () => void;
  sourceJeId?: string;
  anchor?: string;
}

/**
 * This is the base class of the FORC (Feature Oriented Route Component)
 */
export default abstract class ForcBaseComponent<
  Args extends ForcBaseArgs = ForcBaseArgs
> extends Component<Args> {
  @service declare errors: errors;
  @service declare router: RouterService;

  afterCancel = this.args.afterCancel ?? (() => {});
  afterCompleted = this.args.afterCompleted ?? (() => {});
  applicationAdapter = getOwner(this)?.lookup(
    "adapter:application"
  ) as ApplicationAdapter;
  cancelButtonTitle: string = this.args.cancelButtonTitle || "Fermer";
  shouldForceQuit = false;
  submitButtonTitle: string = this.args.submitButtonTitle || "Sauvegarder";

  get formAction(): FormAction {
    return this.isReadOnly
      ? FormAction.Show
      : this.args.formAction || FormAction.New;
  }

  get isDirty(): boolean {
    return true;
  }

  /// ABSTRACT PROPERTIES
  /* Automatically provided by the controller */
  get isReadOnly(): boolean {
    return this.args.isReadOnly || false;
  }

  get originJournalEventId(): string | undefined {
    return this.args.originJournalEventId;
  }

  get originJournalEventType(): string | undefined {
    return this.args.originJournalEventType;
  }

  get patient(): ForcBaseArgs["patient"] {
    return this.args.patient;
  }

  /**
   * Can be set to `false` to disable form dirtyness check to disable the submit
   * button.
   * This is especially useful when chaining forms.
   */
  get shouldCheckDirtyness(): boolean {
    return this.args.shouldCheckDirtyness !== undefined
      ? this.args.shouldCheckDirtyness
      : true;
  }

  constructor(owner: Owner, args: Args) {
    super(owner, args);
    next(() => {
      next(() => {
        if (args.anchor) {
          const elementToScrollTo = document.getElementById(args.anchor);
          elementToScrollTo?.scrollIntoView({ behavior: "smooth" });
        }
      });
    });
    // Exiting the Forc is not only done by the form buttons
    // It could also happen when the user clicks on a link in the UI.
    // The Forc must then be informed when the router will transition
    // and in turn, alert the user if there are pending changes.
    this.router.on("routeWillChange", this.showPendingChangesetAlert);
  }

  beforeValidation(): void {
    return;
  }

  async isValid(): Promise<boolean> {
    return Promise.resolve(true);
  }

  onCancel = task(async () => {
    await this.afterCancel();
  });

  onSubmit = task(async (_event?: SubmitEvent) => {
    this.beforeValidation();
    const isValid = await this.isValid();
    if (!isValid) {
      await this.errors.displayError(
        "Veuillez corriger les erreurs avant de sauvegarder !"
      );
      return;
    }

    await this.save();

    this.shouldForceQuit = true;

    if (this.afterCompleted) {
      await this.afterCompleted();
    }
  });

  async save(): Promise<void> {
    return;
  }

  override willDestroy(): void {
    // https://github.com/emberjs/ember.js/blob/v3.28.1/packages/%40ember/-internals/runtime/lib/mixins/evented.js#L124
    // https://api.emberjs.com/ember/3.28/classes/Evented/methods/off?anchor=off
    // https://api.emberjs.com/ember/3.28/functions/@ember%2Fobject%2Fevents/removeListener
    // https://balinterdi.com/blog/warning-about-losing-unsaved-changes-in-ember-octane/
    this.router.off("routeWillChange", this.showPendingChangesetAlert);
    super.willDestroy();
  }

  protected showPendingChangesetAlert = async (transition: Transition) => {
    if (!this.isDirty) return;

    // Reported here: https://github.com/emberjs/ember.js/issues/19076
    if (transition.isAborted) return;

    // This will handle the case where only the query param changes.
    // This case can happen when the Forc is displayed in a tab.
    if (transition.from?.name == transition.to?.name) return;

    if (this.shouldForceQuit) {
      return;
    }

    transition.abort();

    const response = await this.errors.showPendingChangesetError();
    if (response.isDismissed) return;

    // The user really want to loose its modifications.
    this.shouldForceQuit = true;
    // This code would be better, but not accessible with the current
    // architecture of the Forc: this.args.changeset.rollback();
    transition.retry();
  };
}
